import type Owner from '@ember/owner';
import { tracked } from '@glimmer/tracking';

import { SheetColumnType, SheetWizardStep } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import SheetSelection from 'webapp/utils/sheet-selection';

import type { Args as BaseArgs } from 'webapp/components/custom-query/setup/wizard/base';
import CustomQuerySetupWizardBaseComponent from 'webapp/components/custom-query/setup/wizard/base';
import CustomQuerySetupWizardSheetStepDateComponent from 'webapp/components/custom-query/setup/wizard/sheet/step-date';
import CustomQuerySetupWizardSheetStepDimensionComponent from 'webapp/components/custom-query/setup/wizard/sheet/step-dimension';
import CustomQuerySetupWizardSheetStepMetricComponent from 'webapp/components/custom-query/setup/wizard/sheet/step-metric';
import CustomQuerySetupWizardSheetStepMetricDefinitionComponent from 'webapp/components/custom-query/setup/wizard/sheet/step-metric-definition';
import CustomQuerySetupWizardSheetStepVerifyComponent from 'webapp/components/custom-query/setup/wizard/sheet/step-verify';

import type CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';
import type DataSheet from 'webapp/models-custom/data-sheet';

interface Args extends BaseArgs {
	form: CustomMetricSetupFormSheet;
}

export default class CustomQuerySetupWizardSheetsComponent extends CustomQuerySetupWizardBaseComponent<Args> {
	@tracked stepId: SheetWizardStep = SheetWizardStep.VERIFY;

	wizardType = 'sheet';

	allNavigationSteps = [
		{
			id: SheetWizardStep.VERIFY,
			text: 'Validate data',
			component: CustomQuerySetupWizardSheetStepVerifyComponent,
		},
		{
			id: SheetWizardStep.METRIC,
			text: 'Select value',
			component: CustomQuerySetupWizardSheetStepMetricComponent,
		},
		{
			id: SheetWizardStep.DIMENSION,
			text: 'Segment by dimension',
			component: CustomQuerySetupWizardSheetStepDimensionComponent,
		},
		{
			id: SheetWizardStep.DATE,
			text: 'Select date',
			component: CustomQuerySetupWizardSheetStepDateComponent,
		},
		{
			id: SheetWizardStep.METRIC_DEFINITION,
			text: 'Describe metric',
			component: CustomQuerySetupWizardSheetStepMetricDefinitionComponent,
		},
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const datasource = this.args.model.sourceObj?.sourceType.key;
		if (datasource) {
			this.analyticsService.event(TrackingEvent.QUERY_CREATE_WIZARD_STARTED, {
				origin: this.args.origin,
				type: this.args.model.trackingWizardType,
				datasource,
			});
		}
	}

	override get showNavigation(): boolean {
		return this.stepId !== SheetWizardStep.VERIFY;
	}

	get isDimensionStepOptional(): boolean {
		return !this.args.form.sheets.some((sheet: DataSheet) => {
			return sheet.columnsForDimension.length > 0;
		});
	}

	get isDateStepOptional(): boolean {
		return !this.args.form.sheets.some((sheet: DataSheet) => {
			return sheet.columnsForDate.length > 0;
		});
	}

	goToPrevOrNextStep(fromStepId: string, isPrev: boolean): void {
		const offset = isPrev ? -1 : 1,
			navigationSteps = this.navigationSteps,
			currentNavigationStep = navigationSteps.find((item) => item.id === fromStepId);

		if (currentNavigationStep) {
			const currentStepIdx = navigationSteps.indexOf(currentNavigationStep),
				prevOrNextStep = navigationSteps[currentStepIdx + offset];

			if (prevOrNextStep) {
				if (
					(prevOrNextStep.id === SheetWizardStep.DIMENSION &&
						this.isDimensionStepOptional) ||
					(prevOrNextStep.id === SheetWizardStep.DATE && this.isDateStepOptional)
				) {
					this.goToPrevOrNextStep(prevOrNextStep.id, isPrev);
					return;
				}
				// all good, actually go
				this.goToStep(prevOrNextStep.id);
			} else {
				// no valid step found
				// only handle next - previous step is not possible atm
				if (!isPrev) {
					// next step - query is saved, go forward
					this.args.afterQuerySave();
				}
			}
		}
	}

	selectInitialStep(): void {
		let initialStep = SheetWizardStep.VERIFY; // default - start

		const metricField = this.args.form.getA1FieldForType(SheetColumnType.METRIC),
			dimensionField = this.args.form.getA1FieldForType(SheetColumnType.DIMENSION),
			dateField = this.args.form.getA1FieldForType(SheetColumnType.DATE);

		// clear invalid values (not whole columns, etc)
		if (dimensionField?.value) {
			if (!this.isFieldValidAndWholeColumns(dimensionField) || this.isDimensionStepOptional) {
				dimensionField.value = '';
				dimensionField.updateValueFormatted();
			}
		}

		if (dateField?.value) {
			if (!this.isFieldValidAndWholeColumns(dateField) || this.isDimensionStepOptional) {
				dateField.value = '';
				dateField.updateValueFormatted();
			}
		}

		if (metricField?.value) {
			if (this.isFieldValidAndWholeColumns(metricField)) {
				// if dimension empty but there are columns for dimension
				if (!dimensionField?.value && !this.isDimensionStepOptional) {
					initialStep = SheetWizardStep.DIMENSION;
				} else {
					// if date empty but there are columns for date
					if (!dateField?.value && !this.isDateStepOptional) {
						initialStep = SheetWizardStep.DATE;
					} else {
						// going to settings
						initialStep = SheetWizardStep.METRIC_DEFINITION;
					}
				}
			} else {
				// if metric is not a valid whole column selection, go to metric step
				metricField.value = '';
				metricField.updateValueFormatted();
				initialStep = SheetWizardStep.METRIC;
			}
		}

		this.goToStep(initialStep);
	}

	trackWizardStep(): void {
		const pageNameMap = {
			[SheetWizardStep.VERIFY]: 'Verify',
			[SheetWizardStep.METRIC]: 'Value',
			[SheetWizardStep.DIMENSION]: 'Dimension Value',
			[SheetWizardStep.DATE]: 'Date',
			[SheetWizardStep.METRIC_DEFINITION]: 'Settings',
		};
		// tracking as pageview
		this.analyticsService.page(`Query Wizard ${pageNameMap[this.stepId]}`);
	}

	private isFieldValidAndWholeColumns(a1Field: CustomMetricSetupFormItemField): boolean {
		if (a1Field && a1Field.value && a1Field.isValidAndValidated) {
			const sheetSelection = SheetSelection.fromA1(a1Field.value as string);
			return sheetSelection.isWholeCols;
		} else {
			return false;
		}
	}
}
